import { useState } from 'react'
import { PageHeader } from '../components/PageHeader'
import { TabNavigation } from '../components/TabNavigation'
import { 
  PlusIcon, 
  BanknotesIcon, 
  ReceiptPercentIcon,
  CheckCircleIcon,
  ClockIcon,
  XMarkIcon,
  PhotoIcon,
  UserIcon
} from '@heroicons/react/24/outline'

interface ExpenseItem {
  studentId: string
  studentName: string
  amount: number
  description: string
}

interface Expense {
  id: string
  date: string
  category: 'snacks' | 'delivery' | 'supplies' | 'other'
  totalAmount: number
  items: ExpenseItem[]
  receipt?: File
  status: 'pending' | 'notified' | 'paid'
  createdAt: string
}

export function AdminExpensesPage() {
  const [expenses, setExpenses] = useState<Expense[]>([
    {
      id: '1',
      date: '2024-08-17',
      category: 'snacks',
      totalAmount: 245,
      items: [
        { studentId: 'STU-001', studentName: 'น้องใหม่', amount: 55, description: 'ข้าวกล่อง, น้ำ' },
        { studentId: 'STU-002', studentName: 'น้องพิณ', amount: 50, description: 'ขนม, โค้ก' },
        { studentId: 'STU-003', studentName: 'น้องแนน', amount: 140, description: 'KFC Delivery' }
      ],
      status: 'notified',
      createdAt: '2024-08-17T12:30:00'
    },
    {
      id: '2',
      date: '2024-08-17',
      category: 'supplies',
      totalAmount: 180,
      items: [
        { studentId: 'STU-004', studentName: 'น้องมิ้ง', amount: 90, description: 'ปากกา, ยางลบ' },
        { studentId: 'STU-005', studentName: 'น้องแก้ว', amount: 90, description: 'ปากกา, ยางลบ' }
      ],
      status: 'pending',
      createdAt: '2024-08-17T10:15:00'
    }
  ])

  const [showAddForm, setShowAddForm] = useState(false)
  const [newExpense, setNewExpense] = useState({
    category: 'snacks' as const,
    selectedStudents: [] as string[],
    splitMethod: 'individual' as 'equal' | 'individual',
    totalAmount: '',
    items: [] as { studentId: string; amount: string; description: string }[],
    receipt: null as File | null
  })

  // Mock student data
  const students = [
    { id: 'STU-001', name: 'น้องใหม่' },
    { id: 'STU-002', name: 'น้องพิณ' },
    { id: 'STU-003', name: 'น้องแนน' },
    { id: 'STU-004', name: 'น้องมิ้ง' },
    { id: 'STU-005', name: 'น้องแก้ว' },
    { id: 'STU-006', name: 'น้องออม' }
  ]

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const thaiMonths = [
      'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
      'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
    ]
    const thaiYear = date.getFullYear() + 543
    return `${date.getDate()} ${thaiMonths[date.getMonth()]} ${thaiYear}`
  }

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })
  }

  const categoryLabels = {
    snacks: 'ค่าขนม/เครื่องดื่ม',
    delivery: 'ค่าอาหาร Delivery',
    supplies: 'อุปกรณ์การเรียน',
    other: 'อื่นๆ'
  }

  const statusLabels = {
    pending: { label: 'รอบันทึก', color: 'bg-gray-100 text-gray-800', icon: ClockIcon },
    notified: { label: 'แจ้งแล้ว', color: 'bg-blue-100 text-blue-800', icon: BanknotesIcon },
    paid: { label: 'ชำระแล้ว', color: 'bg-green-100 text-green-800', icon: CheckCircleIcon }
  }

  const handleStudentSelect = (studentId: string) => {
    setNewExpense(prev => ({
      ...prev,
      selectedStudents: prev.selectedStudents.includes(studentId)
        ? prev.selectedStudents.filter(id => id !== studentId)
        : [...prev.selectedStudents, studentId],
      items: prev.selectedStudents.includes(studentId)
        ? prev.items.filter(item => item.studentId !== studentId)
        : [...prev.items, { studentId, amount: '', description: '' }]
    }))
  }

  const updateItemAmount = (studentId: string, amount: string) => {
    setNewExpense(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.studentId === studentId ? { ...item, amount } : item
      )
    }))
  }

  const updateItemDescription = (studentId: string, description: string) => {
    setNewExpense(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.studentId === studentId ? { ...item, description } : item
      )
    }))
  }

  const handleReceiptUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setNewExpense(prev => ({ ...prev, receipt: e.target.files![0] }))
    }
  }

  const handleSubmit = () => {
    const totalCalculated = newExpense.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0)
    
    const newExpenseRecord: Expense = {
      id: Date.now().toString(),
      date: new Date().toISOString().split('T')[0],
      category: newExpense.category,
      totalAmount: totalCalculated,
      items: newExpense.items.map(item => ({
        studentId: item.studentId,
        studentName: students.find(s => s.id === item.studentId)?.name || '',
        amount: parseFloat(item.amount) || 0,
        description: item.description
      })),
      receipt: newExpense.receipt || undefined,
      status: 'pending',
      createdAt: new Date().toISOString()
    }

    setExpenses(prev => [newExpenseRecord, ...prev])
    setShowAddForm(false)
    setNewExpense({
      category: 'snacks',
      selectedStudents: [],
      splitMethod: 'individual',
      totalAmount: '',
      items: [],
      receipt: null
    })
  }


  if (showAddForm) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader 
          title="บันทึกค่าใช้จ่าย"
          showBackButton={true}
          onBack={() => setShowAddForm(false)}
        />
        
        <div className="max-w-3xl mx-auto px-4 py-6">
          {/* Category Selection */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <h3 className="font-medium text-gray-900 mb-3">ประเภทค่าใช้จ่าย</h3>
            <div className="grid grid-cols-2 gap-3">
              {(['snacks', 'delivery', 'supplies', 'other'] as const).map((category) => (
                <button
                  key={category}
                  onClick={() => setNewExpense(prev => ({ ...prev, category }))}
                  className={`p-3 rounded-lg border-2 text-left transition-colors ${
                    newExpense.category === category
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium text-gray-900">{categoryLabels[category]}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Student Selection */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <h3 className="font-medium text-gray-900 mb-3">เลือกนักเรียน</h3>
            <div className="grid grid-cols-2 gap-2">
              {students.map((student) => (
                <label key={student.id} className="flex items-center p-2 rounded-lg hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={newExpense.selectedStudents.includes(student.id)}
                    onChange={() => handleStudentSelect(student.id)}
                    className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <UserIcon className="h-4 w-4 text-gray-400 ml-2 mr-1" />
                  <span className="text-sm font-medium text-gray-700">{student.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Amount Details */}
          {newExpense.selectedStudents.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <h3 className="font-medium text-gray-900 mb-3">รายละเอียดค่าใช้จ่าย</h3>
              <div className="space-y-4">
                {newExpense.items.map((item) => {
                  const student = students.find(s => s.id === item.studentId)
                  return (
                    <div key={item.studentId} className="border border-gray-200 rounded-lg p-3">
                      <div className="font-medium text-gray-900 mb-2">{student?.name}</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">จำนวนเงิน (บาท)</label>
                          <input
                            type="number"
                            value={item.amount}
                            onChange={(e) => updateItemAmount(item.studentId, e.target.value)}
                            placeholder="0"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">รายการ</label>
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => updateItemDescription(item.studentId, e.target.value)}
                            placeholder="เช่น ข้าวกล่อง, น้ำ"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                    </div>
                  )
                })}
                
                <div className="border-t border-gray-200 pt-3">
                  <div className="text-lg font-semibold text-gray-900">
                    รวมทั้งหมด: ฿{newExpense.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Receipt Upload */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <h3 className="font-medium text-gray-900 mb-3">แนบใบเสร็จ/หลักฐาน</h3>
            {newExpense.receipt ? (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <ReceiptPercentIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-700">{newExpense.receipt.name}</span>
                </div>
                <button
                  onClick={() => setNewExpense(prev => ({ ...prev, receipt: null }))}
                  className="text-red-600 hover:text-red-800"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <label className="block">
                <div className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 cursor-pointer transition-colors">
                  <PhotoIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-600">อัพโหลดใบเสร็จ</span>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleReceiptUpload}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={() => setShowAddForm(false)}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              ยกเลิก
            </button>
            <button
              onClick={handleSubmit}
              disabled={newExpense.selectedStudents.length === 0 || newExpense.items.some(item => !item.amount)}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              บันทึกค่าใช้จ่าย
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader 
        title="จัดการค่าใช้จ่าย"
        showBackButton={true}
      />

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Quick Add Button */}
        <div className="mb-6">
          <button
            onClick={() => setShowAddForm(true)}
            className="w-full sm:w-auto bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            บันทึกค่าใช้จ่ายใหม่
          </button>
        </div>

        {/* Today's Summary */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <h3 className="font-medium text-gray-900 mb-3">สรุปค่าใช้จ่ายวันนี้</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">฿425</div>
              <div className="text-sm text-gray-600">รวมทั้งหมด</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">1</div>
              <div className="text-sm text-gray-600">รอแจ้งผู้ปกครอง</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">1</div>
              <div className="text-sm text-gray-600">แจ้งแล้ว</div>
            </div>
          </div>
        </div>

        {/* Expenses List */}
        <div className="space-y-4">
          {expenses.map((expense) => {
            const statusConfig = statusLabels[expense.status]
            const StatusIcon = statusConfig.icon
            
            return (
              <div key={expense.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {categoryLabels[expense.category]} - ฿{expense.totalAmount}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {formatDate(expense.date)} เวลา {formatTime(expense.createdAt)}
                      </p>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {statusConfig.label}
                    </span>
                  </div>

                  {/* Student Items */}
                  <div className="space-y-2 mb-4">
                    {expense.items.map((item, index) => (
                      <div key={index} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center">
                          <UserIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="font-medium text-gray-900">{item.studentName}</span>
                          <span className="text-sm text-gray-600 ml-2">• {item.description}</span>
                        </div>
                        <span className="font-medium text-gray-900">฿{item.amount}</span>
                      </div>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end space-x-2 pt-3 border-t border-gray-200">
                    {expense.status === 'pending' && (
                      <button
                        onClick={() => {
                          setExpenses(prev => prev.map(e => 
                            e.id === expense.id ? { ...e, status: 'notified' } : e
                          ))
                        }}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                      >
                        แจ้งผู้ปกครอง
                      </button>
                    )}
                    {expense.status === 'notified' && (
                      <button
                        onClick={() => {
                          setExpenses(prev => prev.map(e => 
                            e.id === expense.id ? { ...e, status: 'paid' } : e
                          ))
                        }}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                      >
                        ยืนยันชำระแล้ว
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Empty State */}
        {expenses.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <BanknotesIcon className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">ยังไม่มีค่าใช้จ่าย</h3>
            <p className="text-gray-500 mb-4">เริ่มต้นบันทึกค่าใช้จ่ายแรกของคุณ</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors inline-flex items-center"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              บันทึกค่าใช้จ่าย
            </button>
          </div>
        )}
      </div>

      <TabNavigation activeTab="expenses" />
    </div>
  )
}